/**
 * Per-platform caption resolution.
 *
 * A `social_posts` row carries one shared `content` plus an optional
 * `platform_captions` map of per-platform overrides (e.g. a short X version vs a
 * long LinkedIn version). Resolution is always `override ?? shared` so every
 * existing post — and any platform without an override — keeps using the shared
 * caption. Empty/whitespace overrides fall back to the shared content too.
 *
 * Used by the post inspector (display + edit), the immediate publish path
 * (PublishService), and the scheduled dispatcher (post-dispatcher edge fn keeps
 * its own copy of this tiny rule).
 */

export type PlatformCaptions = Record<string, string>;

/** Parse the raw jsonb column into a typed map (defensive against bad shapes). */
export function parsePlatformCaptions(raw: unknown): PlatformCaptions {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: PlatformCaptions = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

/** The caption that should actually publish to `platform`. */
export function resolvePlatformCaption(
  sharedContent: string | null | undefined,
  platformCaptions: unknown,
  platform: string,
): string {
  const overrides = parsePlatformCaptions(platformCaptions);
  const override = overrides[platform];
  if (typeof override === 'string' && override.trim().length > 0) return override;
  return sharedContent ?? '';
}

/** True when `platform` has a non-empty override distinct from the shared caption. */
export function hasPlatformOverride(
  sharedContent: string | null | undefined,
  platformCaptions: unknown,
  platform: string,
): boolean {
  const overrides = parsePlatformCaptions(platformCaptions);
  const override = overrides[platform];
  return (
    typeof override === 'string' && override.trim().length > 0 && override !== (sharedContent ?? '')
  );
}
